import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CriminalCoverNoteModal } from '../../components/coverNote/CriminalCoverNoteModal';
import CommonCoverNoteModal from '../../components/wizard/CommonCoverNoteModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { TaskCaseRow } from '../../data/tasks';
import {
  TASK_EMPTY_DISPLAY,
  taskStatusPillClass,
  urgencyTextClass,
  type TaskListScope,
} from '../../data/taskStatus';
import {
  deleteTaskDraft,
  duplicateTaskCase,
  fetchTaskColumnSuggestions,
  listTasks,
  TASK_TAB_COUNT_PAGE_SIZE,
} from '../../services/tasks';
import { useContinueDraftCase } from '../../hooks/useContinueDraftCase';
import { isCaseRequestUuid } from '../task-case/taskCaseUpdateHelpers';
import CriminalAppellateCoverNoteModal from '../wizard/criminalAppellate/CriminalAppellateCoverNoteModal';
import RightToInformationCoverNoteModal from '../wizard/rightToInformation/RightToInformationCoverNoteModal';
import { setFlashToast } from '../../utils/flashToast';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import {
  usesCommonCoverNote,
  usesCriminalAppellateCoverNote,
  wizardFlowForCategoryName,
} from '../../utils/wizardCategoryRoute';
import { taskCaseRowHighlightClass } from '../../utils/taskRowHighlight';

const ANIM_FRAMES = ['.', '..', '...'] as const;
const EMPTY = TASK_EMPTY_DISPLAY;
const PAGE_RETURN_TO = '/tasks/case-details';

type FlatTab = 'active' | 'created' | 'completed';

type TabCounts = {
  active: number;
  created: number;
  completed: number;
};

function scopeForTab(tab: FlatTab): TaskListScope {
  if (tab === 'active') return 'ma-active';
  if (tab === 'created') return 'active-created';
  return 'ma-completed';
}

function taskRowKey(r: TaskCaseRow): string {
  const caseId = r.caseRequestId ?? r.id;
  const taskId = r.userTaskId?.trim();
  return taskId ? `${caseId}:${taskId}` : caseId;
}

export default function ManagementAssistantCaseDetailsPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { canUpdate, taskCasePath } = useTaskCaseDetailsNavigation();
  const { continueDraftId, handleContinueDraft } = useContinueDraftCase(PAGE_RETURN_TO);

  const [tab, setTab] = useState<FlatTab>('active');
  const [rows, setRows] = useState<TaskCaseRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<TabCounts>({ active: 0, created: 0, completed: 0 });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'ref', dir: 'asc' });

  const [coverRow, setCoverRow] = useState<TaskCaseRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TaskCaseRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  const [animFrame, setAnimFrame] = useState(0);
  const anyActionLoading = duplicateId !== null || continueDraftId !== null || deleteSubmitting;

  useEffect(() => {
    if (!anyActionLoading) return;
    const id = setInterval(() => setAnimFrame((f) => (f + 1) % ANIM_FRAMES.length), 400);
    return () => clearInterval(id);
  }, [anyActionLoading]);

  const dots = ANIM_FRAMES[animFrame];

  const fetchAbortRef = useRef<AbortController | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef<Record<string, string>>({});
  const scope = scopeForTab(tab);

  const refreshTabCounts = useCallback(() => {
    const ac = new AbortController();
    void Promise.all([
      listTasks({ scope: 'ma-active', page: 0, pageSize: TASK_TAB_COUNT_PAGE_SIZE, signal: ac.signal }),
      listTasks({ scope: 'active-created', page: 0, pageSize: TASK_TAB_COUNT_PAGE_SIZE, signal: ac.signal }),
      listTasks({ scope: 'ma-completed', page: 0, pageSize: TASK_TAB_COUNT_PAGE_SIZE, signal: ac.signal }),
    ])
      .then(([active, created, completed]) => {
        setTabCounts({
          active: active.total,
          created: created.total,
          completed: completed.total,
        });
      })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  const fetchList = useCallback(
    (p: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc', listScope: TaskListScope) => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      const { signal } = fetchAbortRef.current;
      setLoading(true);
      setError(null);

      void listTasks({
        scope: listScope,
        page: p - 1,
        pageSize: size,
        columnFilters: filters,
        sortBy: sortKey,
        sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
        signal,
      })
        .then((result) => {
          if (signal.aborted) return;
          if (p > result.totalPages && result.totalPages > 0) {
            setPage(result.totalPages);
            return;
          }
          setRows(result.items);
          setTotalCount(result.total);
          setTotalPages(result.totalPages);
          setTabCounts((prev) => {
            if (listScope === 'ma-active') return { ...prev, active: result.total };
            if (listScope === 'active-created') return { ...prev, created: result.total };
            return { ...prev, completed: result.total };
          });
        })
        .catch(() => {
          if (signal.aborted) return;
          setError(t('tasks.managementAssistant.loadFailed'));
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [t],
  );

  const fetchRef = useRef(fetchList);
  useEffect(() => {
    fetchRef.current = fetchList;
  }, [fetchList]);

  useEffect(() => {
    const abortCounts = refreshTabCounts();
    return abortCounts;
  }, [refreshTabCounts]);

  useEffect(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir, scope);
    return () => fetchAbortRef.current?.abort();
  }, [page, pageSize, sort.key, sort.dir, scope]);

  const resetListState = useCallback(() => {
    setPage(1);
    filtersRef.current = {};
    setSort({ key: 'ref', dir: 'asc' });
  }, []);

  const selectTab = useCallback(
    (next: FlatTab) => {
      if (next === tab) return;
      setTab(next);
      resetListState();
    },
    [resetListState, tab],
  );

  const handleFiltersChange = useCallback(
    (colFilters: Record<string, string>) => {
      filtersRef.current = colFilters;
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        setPage(1);
        fetchRef.current(1, pageSize, colFilters, sort.key, sort.dir, scope);
      }, 400);
    },
    [pageSize, sort.key, sort.dir, scope],
  );

  const handleSortChange = useCallback((key: string, dir: 'asc' | 'desc') => {
    setSort({ key, dir });
    setPage(1);
  }, []);

  const reloadList = useCallback(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir, scope);
    refreshTabCounts();
  }, [page, pageSize, sort.key, sort.dir, scope, refreshTabCounts]);

  const handleDuplicate = useCallback(
    (row: TaskCaseRow) => {
      const id = row.caseRequestId ?? row.id;
      if (duplicateId) return;
      setDuplicateId(id);
      void duplicateTaskCase(id)
        .then((newId) => {
          if (!newId) {
            setFlashToast({ message: t('tasks.cases.duplicateFailed'), variant: 'error' });
            return;
          }
          setFlashToast({ message: t('tasks.cases.duplicateSuccess'), variant: 'success' });
          handleContinueDraft({
            id: newId,
            ref: newId,
            fileCategory: row.fileCategory !== EMPTY ? row.fileCategory : undefined,
          });
        })
        .finally(() => setDuplicateId(null));
    },
    [duplicateId, handleContinueDraft, t],
  );

  const runDeleteDraft = () => {
    if (!confirmDelete || deleteSubmitting) return;
    const id = confirmDelete.caseRequestId ?? confirmDelete.id;
    setDeleteSubmitting(true);
    void deleteTaskDraft(id)
      .then((ok) => {
        if (!ok) {
          setFlashToast({ message: t('tasks.cases.deleteDraftFailed'), variant: 'error' });
          return;
        }
        setFlashToast({ message: t('tasks.cases.deleteDraftSuccess'), variant: 'success' });
        setConfirmDelete(null);
        reloadList();
      })
      .finally(() => setDeleteSubmitting(false));
  };

  const isDraft = (r: TaskCaseRow) => {
    const k = (r.statusKey ?? '').trim().toLowerCase();
    if (k === 'draft') return true;
    const s = (r.status ?? '').trim().toLowerCase();
    return s === 'draft';
  };

  const handleOpenCoverNote = useCallback(
    (row: TaskCaseRow) => {
      const caseRequestId = row.caseRequestId ?? row.id;
      if (!isCaseRequestUuid(caseRequestId)) {
        setFlashToast({ message: t('caseRequests.coverNoteSubmittedOnly'), variant: 'error' });
        return;
      }
      setCoverRow(row);
    },
    [t],
  );

  const coverFlow = coverRow ? wizardFlowForCategoryName(coverRow.fileCategory) : 'unknown';
  const coverCaseRequestId = coverRow ? (coverRow.caseRequestId ?? coverRow.id) : '';
  const coverFileRef = coverRow?.ref;

  const columns = useMemo<Column<TaskCaseRow>[]>(() => {
    const base: Column<TaskCaseRow>[] = [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        filterPattern: /^[a-zA-Z0-9/._ -]*$/,
        render: (r) => {
          const draft = isDraft(r);
          const viewOnly = tab !== 'active' || draft || !canUpdate;
          return (
            <span className="rn">
              <Link
                to={taskCasePath({
                  caseRequestId: r.caseRequestId ?? r.id,
                  userTaskId: r.userTaskId,
                  viewMode: viewOnly,
                })}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {r.ref}
              </Link>
            </span>
          );
        },
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('tasks.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => (
          <span style={{ color: r.subFileCategory === EMPTY ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>
        ),
      },
      {
        key: 'agency',
        header: t('tasks.shared.submittedAgency'),
        getValue: (r) => r.agency,
        render: (r) => <span style={{ color: r.agency === EMPTY ? 'var(--t3)' : undefined }}>{r.agency}</span>,
      },
      {
        key: 'status',
        header: t('tasks.shared.taskStatus'),
        getValue: (r) => r.status,
        render: (r) =>
          r.status === EMPTY ? (
            <span className="t-dash">{EMPTY}</span>
          ) : (
            <span className={`pill ${taskStatusPillClass(r.statusKey)}`}>{r.status}</span>
          ),
      },
      {
        key: 'submittedAt',
        header: t('tasks.managementAssistant.createdAt'),
        filterType: 'date',
        getValue: (r) => r.submittedAtIso,
        render: (r) => (
          <span style={{ color: r.submittedAt === EMPTY ? 'var(--t3)' : undefined }}>{r.submittedAt}</span>
        ),
      },
      {
        key: 'urgency',
        header: t('tasks.shared.urgencyLevel'),
        getValue: (r) => r.urgencyLevel,
        render: (r) => <span className={urgencyTextClass(r.urgencyLevel)}>{r.urgencyLevel}</span>,
      },
      {
        key: 'officer',
        header: t('tasks.shared.assignedOfficer'),
        getValue: (r) => r.assignedOfficer ?? '',
        render: (r) => (
          <span style={{ color: r.assignedOfficer === EMPTY ? 'var(--t3)' : undefined }}>
            {r.assignedOfficer}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => {
          const rowId = r.caseRequestId ?? r.id;
          const isDuplicating = duplicateId === rowId;
          const isContinuing = continueDraftId === rowId;
          const draft = isDraft(r);

          const duplicateBtn = !draft ? (
            <button
              className="act-b"
              type="button"
              data-tip={isDuplicating ? `${t('tasks.shared.duplicating')}${dots}` : t('tasks.shared.duplicateCase')}
              aria-label={isDuplicating ? `${t('tasks.shared.duplicating')}${dots}` : t('tasks.shared.duplicateCase')}
              disabled={isDuplicating || duplicateId !== null}
              onClick={() => handleDuplicate(r)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          ) : null;

          const coverNoteBtn = !draft ? (
            <button
              className="act-b"
              type="button"
              data-tip={t('tasks.shared.downloadCoverNote')}
              aria-label={t('tasks.shared.downloadCoverNote')}
              onClick={() => handleOpenCoverNote(r)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          ) : null;

          if (draft && (tab === 'active' || tab === 'created')) {
            return (
              <div className="act-wrap">
                <button
                  className="act-b"
                  type="button"
                  data-tip={isContinuing ? `${t('tasks.shared.opening')}${dots}` : t('tasks.shared.continueDraft')}
                  aria-label={isContinuing ? `${t('tasks.shared.opening')}${dots}` : t('tasks.shared.continueDraft')}
                  disabled={isContinuing || continueDraftId !== null}
                  onClick={() =>
                    handleContinueDraft({
                      id: rowId,
                      ref: r.ref,
                      fileCategory: r.fileCategory !== EMPTY ? r.fileCategory : undefined,
                    })
                  }
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  className="act-b"
                  type="button"
                  data-tip={t('tasks.shared.removeDraft')}
                  aria-label={t('tasks.shared.removeDraft')}
                  style={{ color: 'var(--red)' }}
                  onClick={() => setConfirmDelete(r)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </button>
              </div>
            );
          }

          const viewBtn = (
            <button
              className="act-b"
              type="button"
              data-tip={t('tasks.shared.viewTaskCaseDetails')}
              aria-label={t('tasks.shared.viewTaskCaseDetails')}
              onClick={() =>
                nav(
                  taskCasePath({
                    caseRequestId: r.caseRequestId ?? r.id,
                    userTaskId: r.userTaskId,
                    viewMode: true,
                  }),
                )
              }
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          );

          const editBtn = (
            <Link
              to={taskCasePath({
                caseRequestId: r.caseRequestId ?? r.id,
                userTaskId: r.userTaskId,
                viewMode: !canUpdate,
              })}
              className="act-b"
              data-tip={
                canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetails')
              }
              aria-label={
                canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetails')
              }
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
          );

          if (tab === 'active') {
            return (
              <div className="act-wrap">
                {editBtn}
                {duplicateBtn}
                {coverNoteBtn}
              </div>
            );
          }

          return (
            <div className="act-wrap">
              {viewBtn}
              {duplicateBtn}
              {coverNoteBtn}
            </div>
          );
        },
      },
    ];

    return base;
  }, [
    canUpdate,
    continueDraftId,
    dots,
    duplicateId,
    handleContinueDraft,
    handleDuplicate,
    handleOpenCoverNote,
    nav,
    t,
    tab,
    taskCasePath,
  ]);

  return (
    <>
      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={t('tasks.cases.deleteTitle')}
        message={t('tasks.cases.deleteMessage')}
        onClose={() => {
          if (!deleteSubmitting) setConfirmDelete(null);
        }}
        onConfirm={runDeleteDraft}
        confirmDisabled={deleteSubmitting}
        confirmLabel={deleteSubmitting ? t('common.deleting') : t('common.yes')}
      />

      <CriminalCoverNoteModal
        open={!!coverRow && (coverFlow === 'criminal' || coverFlow === 'unknown')}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <CommonCoverNoteModal
        isOpen={!!coverRow && usesCommonCoverNote(coverFlow)}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <CriminalAppellateCoverNoteModal
        isOpen={!!coverRow && usesCriminalAppellateCoverNote(coverFlow)}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <RightToInformationCoverNoteModal
        isOpen={!!coverRow && coverFlow === 'right-to-information'}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />

      <div className="bc">
        <span>{t('tasks.managementAssistant.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('tasks.managementAssistant.title')}</div>
        </div>
        <Link to="/tasks/create-case-request" className="btn-create" style={{ textDecoration: 'none' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('tasks.cases.createCaseRequest')}
        </Link>
      </div>

      <div className="cr-table-wrap">
        <div className="cr-tabs-bar">
          <button
            type="button"
            className={`cr-tab ${tab === 'active' ? 'on' : ''}`}
            onClick={() => selectTab('active')}
          >
            {t('tasks.managementAssistant.tabActive')} <span className="tab-count">{tabCounts.active}</span>
          </button>
          <button
            type="button"
            className={`cr-tab ${tab === 'created' ? 'on' : ''}`}
            onClick={() => selectTab('created')}
          >
            {t('tasks.managementAssistant.tabCreated')} <span className="tab-count">{tabCounts.created}</span>
          </button>
          <button
            type="button"
            className={`cr-tab ${tab === 'completed' ? 'on' : ''}`}
            onClick={() => selectTab('completed')}
          >
            {t('tasks.managementAssistant.tabCompleted')} <span className="tab-count">{tabCounts.completed}</span>
          </button>
        </div>

        <div className="tab-panel on">
          {error ? <div className="si-err" style={{ margin: '12px 18px 0' }}>{error}</div> : null}

          <DataTable
            embedded
            title={t('tasks.managementAssistant.tableTitle')}
            columns={columns}
            rows={rows}
            rowKey={taskRowKey}
            rowClassName={taskCaseRowHighlightClass}
            defaultRowsPerPage={5}
            rowsPerPageOptions={[5, 10, 15]}
            previousLabel={t('tasks.shared.previousBack')}
            nextLabel={t('tasks.shared.nextForward')}
            loading={loading}
            fetchColumnSuggestions={(col, prefix) => fetchTaskColumnSuggestions(scope, col, prefix)}
            onFiltersChange={handleFiltersChange}
            serverSort={{
              key: sort.key,
              dir: sort.dir,
              onSortChange: handleSortChange,
            }}
            serverPaging={{
              totalCount,
              totalPages,
              page,
              pageSize,
              onPageChange: (p) => setPage(p),
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
